import React, { useEffect, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { useParams, Link } from "react-router-dom";
import { FileCheck2, Loader2, XCircle, BadgeCheck, Download, Percent, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const Row = ({ label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-[#2A2A22]">
    <div className="sm:w-56 shrink-0 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
    <div className={`text-[13px] text-[#ECECE4] ${mono ? "font-mono text-[11px] break-all" : ""}`}>{value}</div>
  </div>
);

export default function PublicFundingVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [qr, setQr] = useState("");
  const [disc, setDisc] = useState({ counterparty_name: "", counterparty_entity: "", counterparty_email: "", bank_name: "", note: "" });
  const [discBusy, setDiscBusy] = useState(false);
  const [discDone, setDiscDone] = useState(false);
  const [showDisc, setShowDisc] = useState(false);

  const submitDiscount = async (e) => {
    e.preventDefault();
    setDiscBusy(true);
    try {
      await axios.post(`${API}/funding/drafts/${token}/discount-request`, disc);
      setDiscDone(true);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not submit your request.");
    } finally { setDiscBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(window.location.href, { margin: 1, width: 200, errorCorrectionLevel: "H" }).then((u) => alive && setQr(u)).catch(() => {});
    (async () => {
      try {
        const r = await axios.get(`${API}/funding/verify/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "This draft could not be verified.");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="funding-verify-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/funding" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="fv-home-link">← Funding Registry</Link>
        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22] flex items-center gap-3">
            <FileCheck2 size={22} className="text-[#B8860B]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">SEUTRADE FINANCIAL SERVICES · Private Negotiable Instrument</div>
              <div className="text-xl font-serif">Draft Issuance Verification</div>
            </div>
          </div>
          {error ? (
            <div className="px-7 py-12 text-center" data-testid="fv-error">
              <XCircle size={34} className="text-[#FF8585] mx-auto mb-3" />
              <div className="text-[#FF8585] text-sm">{error}</div>
            </div>
          ) : !data ? (
            <div className="px-7 py-16 text-center" data-testid="fv-loading"><Loader2 className="animate-spin inline text-[#B8860B]" /></div>
          ) : (
            <div className="px-7 py-6" data-testid="fv-result">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 text-[11px] uppercase tracking-[0.14em] font-bold bg-[#0B6B4F]/15 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full" data-testid="fv-verified-badge">
                <BadgeCheck size={14} /> Issuance Verified
              </div>
              <Row label="Draft No." value={<span className="font-semibold text-[#E4C97A]">{data.draft_no}</span>} />
              <Row label="Master Agreement" value={data.program_no} />
              <Row label="Program" value={data.program_name || "—"} />
              <Row label="Drawer" value={data.drawer_name || "—"} />
              <Row label="Drawee / Acceptor" value={data.drawee_name || "—"} />
              <Row label="Payee" value={<span>{data.payee_name}{data.payee_entity ? ` (${data.payee_entity})` : ""}</span>} />
              <Row label="Face Amount" value={fmtUSD(data.face_amount)} />
              <Row label="Discount Rate" value={`${data.discount_rate}%`} />
              <Row label="Discounted Value" value={<span className="text-[#3DBB8E]">{fmtUSD(data.discounted_value)}</span>} />
              <Row label="Issue Date" value={data.issue_date} />
              <Row label="Tenor" value={`${data.tenor_days} days`} />
              <Row label="Maturity Date" value={data.maturity_date} />
              <Row label="Status" value={<span className="uppercase">{data.status}</span>} />
              <Row label="Governing Law" value={data.governing_law || "—"} />
              <Row label="Collateralized" value={data.collateralized ? "Yes" : "No"} />
              {data.medallion_no && <Row label="S.T.A.M.P. Medallion" value={<span className="text-[#E4C97A]">{data.medallion_no}</span>} />}
              {data.reserve_backing && data.reserve_backing.backed && (
                <Row label="Reserve-Backed" value={<span className="text-[#3DBB8E]">{data.reserve_backing.reserve_no} · SEUTRADE FINANCIAL SERVICES Internal Credit Reserve (ratio {Math.round((data.reserve_backing.ratio || 0) * 100)}%)</span>} />
              )}
              {data.vault_id && <Row label="Vault ID" value={<span className="font-mono text-[11px]">{data.vault_id}</span>} />}
              {(data.assignments || []).length > 0 && (
                <div className="py-2.5 border-b border-[#2A2A22]" data-testid="fv-assignments">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-2">Assignments</div>
                  {data.assignments.map((a, i) => (
                    <div key={i} className="text-[12px] text-[#9A9A90]">→ {a.assignee_name}{a.assignee_entity ? ` (${a.assignee_entity})` : ""} · {(a.at || "").slice(0, 10)}</div>
                  ))}
                </div>
              )}
              <Row label="SHA-256" value={data.sha256 || "—"} mono />
              <a href={`${API}/funding/drafts/${token}/certificate.pdf`} target="_blank" rel="noreferrer" data-testid="fv-download-cert" className="mt-5 inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
                <Download size={13} /> Download Private Negotiable Instrument
              </a>
              {data.redemption_token && (
                <a href={`/redeem/${data.redemption_token}`} target="_blank" rel="noreferrer" data-testid="fv-redeem-link" className="mt-5 ml-3 inline-flex items-center gap-2 border border-[#3DBB8E]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/10 px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
                  <BadgeCheck size={13} /> Redeem / Withdraw
                </a>
              )}
              <a href={`${API}/funding/drafts/${token}/ucc1.pdf`} target="_blank" rel="noreferrer" data-testid="fv-ucc1" className="mt-5 ml-3 inline-flex items-center gap-2 border border-[#9DB4E8]/50 text-[#9DB4E8] hover:bg-[#9DB4E8]/10 px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
                <Download size={13} /> UCC-1 (you as secured party)
              </a>

              {["settled", "cancelled"].includes(data.status) ? null : discDone ? (
                <div className="mt-5 flex items-center gap-2 text-[13px] text-[#3DBB8E] bg-[#0B6B4F]/15 border border-[#0B6B4F]/40 rounded-md px-4 py-3" data-testid="fv-discount-done">
                  <CheckCircle2 size={16} /> Your discount request has been received. Our desk will contact you shortly.
                </div>
              ) : data.discount_requested ? (
                <div className="mt-5 flex items-center gap-2 text-[13px] text-[#E4C97A] bg-[#B8860B]/10 border border-[#B8860B]/40 rounded-md px-4 py-3" data-testid="fv-discount-pending">
                  <CheckCircle2 size={16} /> A discount request is already on file for this draft. Our desk is reviewing it.
                </div>
              ) : (
                <div className="mt-5">
                  {!showDisc ? (
                    <button onClick={() => setShowDisc(true)} data-testid="fv-request-discount-btn" className="inline-flex items-center gap-2 border border-[#C9A227]/50 text-[#E4C97A] hover:bg-[#B8860B]/10 px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold">
                      <Percent size={13} /> Request to discount this draft
                    </button>
                  ) : (
                    <form onSubmit={submitDiscount} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-lg p-5 grid sm:grid-cols-2 gap-3" data-testid="fv-discount-form">
                      <div className="sm:col-span-2 text-[12px] uppercase tracking-[0.14em] text-[#8A8A8A]">Request to discount {data.draft_no}</div>
                      <input required placeholder="Your name / contact" value={disc.counterparty_name} onChange={(e) => setDisc((d) => ({ ...d, counterparty_name: e.target.value }))} data-testid="fv-disc-name" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                      <input placeholder="Entity (optional)" value={disc.counterparty_entity} onChange={(e) => setDisc((d) => ({ ...d, counterparty_entity: e.target.value }))} data-testid="fv-disc-entity" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                      <input required type="email" placeholder="Email" value={disc.counterparty_email} onChange={(e) => setDisc((d) => ({ ...d, counterparty_email: e.target.value }))} data-testid="fv-disc-email" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                      <input placeholder="Discounting bank (optional)" value={disc.bank_name} onChange={(e) => setDisc((d) => ({ ...d, bank_name: e.target.value }))} data-testid="fv-disc-bank" className="bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" />
                      <textarea placeholder="Note (optional)" value={disc.note} onChange={(e) => setDisc((d) => ({ ...d, note: e.target.value }))} data-testid="fv-disc-note" className="sm:col-span-2 bg-[#13130E] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#C9A227] outline-none" rows={2} />
                      <div className="sm:col-span-2 flex gap-3">
                        <button type="submit" disabled={discBusy} data-testid="fv-disc-submit" className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-4 py-2 rounded-md text-[11px] uppercase tracking-[0.12em] font-bold disabled:opacity-50">{discBusy ? <Loader2 size={13} className="animate-spin" /> : <Percent size={13} />} Submit request</button>
                        <button type="button" onClick={() => setShowDisc(false)} className="text-[11px] text-[#8A8A8A] hover:text-[#ECECE4]">Cancel</button>
                      </div>
                    </form>
                  )}
                </div>
              )}
              {qr && (
                <div className="mt-5 flex items-center gap-4" data-testid="fv-qr">
                  <img src={qr} alt="verification qr" className="w-24 h-24 rounded bg-white p-2" />
                  <p className="text-[11px] text-[#8A8A8A] leading-relaxed max-w-sm">Scan this QR to re-open this verification at any time. It confirms the issuance of the private negotiable instrument above under its Master Agreement and may be relied upon for discount.</p>
                </div>
              )}
              <p className="mt-5 text-[11px] text-[#5C5C5C] leading-relaxed">
                This read-only certificate confirms the issuance of the referenced private negotiable instrument. It is not itself a negotiable instrument and does not transfer title.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
